// ============================================================
// api/adminTenantSearch.js
// Endpoint bindings for the mgmt/admin tenant-search surface
// (Phase 3 Stage 4 — consent flow 2a.1 + CRUD/broker 2a.2).
//
// Backend reference:
//   backend/func-carehub-api/src/functions/admin-clients-tenant-integration-*.js
//   backend/func-carehub-api/src/functions/admin-clients-tenant-integration-consent-url.js
//   APIM operations: mgmt-admin-clients-tenant-integration-*
//
// ── TWO ERROR CONVENTIONS (do not conflate) ─────────────────
//
// Pattern A — CRUD endpoints THROW on failure. get / patch / revoke /
//   reactivate / search-log / consent-url surface non-2xx as a thrown
//   ApiError (status, message [= the error code], detail, field) per the
//   shared client.js contract. Callers use try/catch (the InvitationsPage
//   pattern — NOT FederationTab's dead err.error mapping; ApiError has no
//   `.error` property).
//   Exception: getTenantIntegration converts a 404 to `null` (the
//   "not configured" case is not an error for the caller).
//
// Pattern B — MCP-broker endpoints (list-sites, test-search) RETURN the
//   body VERBATIM at HTTP 200 for every MCP-call outcome:
//     success → { success: true, ... }
//     failure → { success: false, error: { code, message } }
//   Only an ENDPOINT-level failure (bad params / no row / inactive /
//   the FA itself down) throws an ApiError. So callers check
//   `result.success` AND wrap in try/catch for the HTTP-level case.
// ============================================================

import { apiGet, apiPost, apiPatch, apiPut } from './client';

const base = (clientId) => `/mgmt/admin/clients/${clientId}/tenant-integration`;

// ── CRUD (Pattern A — throws) ───────────────────────────────

/**
 * Fetch the Client's ClientTenantIntegrations row.
 * Converts 404 → null (the "not configured" state); rethrows other errors.
 *
 * @param {number} clientId
 * @returns {Promise<Object|null>} the integration row, or null if none exists.
 */
export async function getTenantIntegration(clientId) {
  try {
    // expectedStatuses: [404] — the "not configured" case is routine on-mount,
    // not an error; suppress the client.js console.error noise for it.
    return await apiGet(base(clientId), {}, { expectedStatuses: [404] });
  } catch (err) {
    if (err && err.status === 404) return null;
    throw err;
  }
}

/**
 * Update ScopedSites and/or IsSearchActive. Both optional; at least one
 * required. Immutable fields + `status` are rejected by the backend.
 * Document indexing is NOT set here - it is a CLIENT-level switch
 * (setClientDocumentIndexing below), ruled 2026-08-10.
 *
 * @param {number} clientId
 * @param {number} tenantIntegrationId
 * @param {{ scopedSites?: Array, isSearchActive?: boolean, reason?: string }} body
 * @returns {Promise<Object>} the updated row.
 */
export async function patchTenantIntegration(clientId, tenantIntegrationId, body) {
  return apiPatch(`${base(clientId)}/${tenantIntegrationId}`, body);
}

/**
 * Revoke (active → revoked). ScopedSites preserved; IsSearchActive reset to
 * 0 server-side. 409 if already revoked.
 *
 * @param {number} clientId
 * @param {number} tenantIntegrationId
 * @param {string} [reason]
 * @returns {Promise<Object>} the updated row.
 */
export async function revokeTenantIntegration(clientId, tenantIntegrationId, reason) {
  return apiPost(`${base(clientId)}/${tenantIntegrationId}/revoke`, reason ? { reason } : {});
}

/**
 * Reactivate (revoked|expired → active). Resets ConsecutiveFailureCount=0;
 * IsSearchActive stays 0 (admin re-toggles explicitly). 409 if already active.
 *
 * @param {number} clientId
 * @param {number} tenantIntegrationId
 * @param {string} [reason]
 * @returns {Promise<Object>} the updated row.
 */
export async function reactivateTenantIntegration(clientId, tenantIntegrationId, reason) {
  return apiPost(`${base(clientId)}/${tenantIntegrationId}/reactivate`, reason ? { reason } : {});
}

/**
 * Paginated TenantSearchLog. Filters are optional; the client query-string
 * builder skips null/undefined/'' values.
 *
 * @param {number} clientId
 * @param {number} tenantIntegrationId
 * @param {{ limit?: number, offset?: number, agentType?: string, fromDate?: string, toDate?: string, onlyErrors?: boolean }} [filters]
 * @returns {Promise<{ items: Array, pagination: { limit: number, offset: number, returned: number } }>}
 */
export async function getSearchLog(clientId, tenantIntegrationId, filters = {}) {
  return apiGet(`${base(clientId)}/${tenantIntegrationId}/search-log`, filters);
}

/**
 * Generate the Broad companion-app admin-consent URL (2a.1). Broad only.
 *
 * @param {number} clientId
 * @param {{ tenantId: string, scopeModel: 'broad' }} body
 * @returns {Promise<{ consentUrl: string, expiresAt: string, correlationId: string }>}
 */
export async function generateConsentUrl(clientId, body) {
  return apiPost(`${base(clientId)}/consent-url`, body);
}

// ── MCP brokers (Pattern B — returns body verbatim) ─────────

/**
 * List the SharePoint sites the companion app can see (the ScopedSites
 * picker source). Returns the body verbatim — check `result.success`.
 *
 * Broad: enumerates via `searchTerm`. Selected (Phase 3a): pass `siteUrls`
 * (admin-supplied URLs/composite IDs) — the MCP tool resolves each directly
 * (Sites.Selected cannot enumerate) and returns `droppedUrls` for the ones
 * not yet granted.
 *
 * @param {number} clientId
 * @param {number} tenantIntegrationId
 * @param {string} [searchTerm]
 * @param {string[]} [siteUrls] Selected URL-resolve mode (one URL/composite ID per entry).
 * @returns {Promise<{ success: true, sites: Array, pseudoSitesFiltered: number, droppedUrls?: Array } | { success: false, error: { code: string, message: string } }>}
 */
export async function listSites(clientId, tenantIntegrationId, searchTerm, siteUrls) {
  const body = {};
  if (searchTerm) body.searchTerm = searchTerm;
  if (Array.isArray(siteUrls) && siteUrls.length > 0) body.siteUrls = siteUrls;
  return apiPost(`${base(clientId)}/${tenantIntegrationId}/list-sites`, body);
}

/**
 * Google Workspace connect flow (2026-09-09) — the Drive twin of listSites.
 * Needs NO integration row. With no options: the service-account address,
 * delegation client ID + scope for the setup checklist. With `domain`
 * (direct-share mode, the default): the Shared Drives the service account
 * has been added to whose organisers are in that domain, plus a COUNT of
 * the ones that are not. With `subject` (delegation fallback): the Shared
 * Drives that account is a member of. Either list is the picker's source
 * and the connection test in one call. Returns the body verbatim — check
 * `result.success`; on failure `error.code` is consent_missing |
 * subject_not_found | not_configured | google_timeout | google_error | mcp_*.
 *
 * @param {number} clientId
 * @param {{ domain?: string, subject?: string }} [options]
 * @returns {Promise<{ success: boolean, configured: boolean|null, serviceAccountEmail: string|null, delegationClientId: string|null, scope: string|null, mode: 'direct'|'delegation'|null, drives: Array<{driveId:string, driveName:string}>|null, unattributedCount: number|null, error: {code:string, message:string}|null }>}
 */
export async function listGoogleDrives(clientId, { domain, subject } = {}) {
  const body = {};
  if (domain) body.domain = domain;
  if (subject) body.subject = subject;
  return apiPost(`/mgmt/admin/clients/${clientId}/google-workspace/drives`, body);
}

/**
 * Create or update the client's Google Workspace integration (2026-09-09).
 * No `subject` = direct-share mode (the service account reads the drives it
 * was added to, as itself); the backend verifies every drive against the
 * client's domain first (400 drive_not_verified). A `subject` = the
 * delegation fallback. 409 integration_provider_conflict when the client
 * holds an m365 row.
 *
 * @param {number} clientId
 * @param {{ domain: string, subject?: string, drives: Array<{driveId:string, driveName?:string}>, isSearchActive?: boolean, reason?: string }} body
 * @returns {Promise<Object>} the full integration row + `created`.
 */
export async function saveGoogleWorkspace(clientId, body) {
  return apiPut(`/mgmt/admin/clients/${clientId}/google-workspace`, body);
}

/**
 * Live diagnostic search (no cache, no log). Returns the body verbatim.
 *
 * @param {number} clientId
 * @param {number} tenantIntegrationId
 * @param {string} [query]
 * @returns {Promise<{ success: boolean, diagnostics: Object|null } | { success: false, error: { code: string, message: string } }>}
 */
export async function testSearch(clientId, tenantIntegrationId, query) {
  return apiPost(`${base(clientId)}/${tenantIntegrationId}/test-search`, query ? { query } : {});
}

// ── Phase 4 — document content indexing ─────────────────────
// NOTE the path: these two are NOT under the tenant-integration base
// (they are client-scoped, not integration-scoped), so they are built from
// /mgmt/admin/clients/{id}/... directly. API_BASE_URL already ends
// /carehub/api, so paths here never repeat the /api prefix (the six dead
// admin surfaces of 2026-08-04 were all a doubled /api).

/**
 * The whole indexing panel in one request: toggle state, aggregate counts,
 * the indexed documents (bounded) and the recent run log (bounded).
 * Converts 404 (no integration) → null, matching getTenantIntegration.
 *
 * @param {number} clientId
 * @returns {Promise<Object|null>}
 */
export async function getIndexingStatus(clientId) {
  try {
    return await apiGet(`/mgmt/admin/clients/${clientId}/indexing-status`, {}, { expectedStatuses: [404] });
  } catch (err) {
    if (err && err.status === 404) return null;
    throw err;
  }
}

/**
 * Queue every eligible document for re-indexing (force=true). The work runs
 * on the chunking queue, so this returns as soon as the messages are queued.
 * 409 when indexing is switched off or the integration is not active.
 *
 * @param {number} clientId
 * @param {{ sourceType?: 'carecomply'|'sharepoint' }} [options]
 * @returns {Promise<Object>} { enqueuedCount, failedCount, detail, ... }
 */
export async function reindexClientDocuments(clientId, options = {}) {
  return apiPost(`/mgmt/admin/clients/${clientId}/indexing-reindex`, options);
}

/**
 * Set the CLIENT-level document indexing switch. Deliberately not on the
 * tenant-integration PATCH: CareComply policy documents live in our own blob
 * storage and need no M365 access, so this must work for a client that has
 * never done an M365 consent ceremony (ruled 2026-08-10).
 *
 * @param {number} clientId
 * @param {boolean} isDocumentIndexingActive
 * @param {string} [reason]
 * @returns {Promise<{clientId:number, isDocumentIndexingActive:boolean, changed:boolean}>}
 */
export async function setClientDocumentIndexing(clientId, isDocumentIndexingActive, reason) {
  const body = { isDocumentIndexingActive };
  if (reason) body.reason = reason;
  return apiPatch(`/mgmt/admin/clients/${clientId}/indexing`, body);
}
